<template>
  <div class="timeline-grid-wrapper">
    <div
      class="timeline-grid"
      :style="gridStyle"
    >
      <!-- ヘッダー行 -->
      <div
        class="timeline-grid__corner"
        :style="{ gridRow: 1, gridColumn: 1 }"
      />
      <div
        v-for="(time, tIdx) in timeSlots"
        :key="`header-${time.value}`"
        class="timeline-grid__header"
        :style="{ gridRow: 1, gridColumn: tIdx + 2 }"
      >
        {{ time.label }}
      </div>

      <template v-for="row in rows">
        <!-- ホールヘッダー -->
        <div
          v-if="row.type === 'hall'"
          :key="`hall-${row.id}`"
          class="timeline-grid__hall-label"
          :style="{ gridRow: row.gridRow, gridColumn: `1 / ${colCount + 1}` }"
        >
          {{ row.name }}
        </div>

        <!-- テーブル行: ラベル + セル -->
        <template v-if="row.type === 'table' || row.type === 'unassigned'">
          <div
            :key="`label-${row.gridRow}`"
            class="timeline-grid__row-label"
            :style="{ gridRow: row.gridRow, gridColumn: 1 }"
          >
            {{ row.label }}
          </div>
          <div
            v-for="(time, tIdx) in timeSlots"
            :key="`cell-${row.gridRow}-${tIdx}`"
            class="timeline-grid__cell"
            :class="{ 'timeline-grid__cell--selected': isCellSelected(row, time) }"
            :style="{ gridRow: row.gridRow, gridColumn: tIdx + 2 }"
            @click="onCellClick(row, time)"
          />

          <!-- 予約ブロック: gridColumnで直接配置 -->
          <reservation-block
            v-for="res in getReservationsForRow(row)"
            :key="`block-${res.id}`"
            :check-in-enabled="checkInEnabled"
            :reservation="res"
            :style="getBlockGridStyle(row, res)"
            @click="$emit('reservation-click', $event)"
            @check-in="$emit('check-in', $event)"
          />
        </template>

        <!-- テーブル未指定ヘッダー -->
        <div
          v-if="row.type === 'unassigned-header'"
          :key="`unassigned-header-${row.gridRow}`"
          class="timeline-grid__hall-label"
          :style="{ gridRow: row.gridRow, gridColumn: `1 / ${colCount + 1}` }"
        >
          テーブル未指定
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ReservationBlock from './ReservationBlock.vue'

export default {
  name: 'TimelineGrid',
  components: {
    ReservationBlock,
  },
  props: {
    // 来店処理（卓作成）を許可するか（ReservationDayBoard から予約日=当営業日のときのみ true）
    checkInEnabled: {
      type: Boolean,
      default: true,
    },
    reservations: {
      type: Array,
      default: () => [],
    },
    halls: {
      type: Array,
      default: () => [],
    },
    startHour: {
      type: Number,
      default: 18,
    },
    endHour: {
      type: Number,
      default: 30,
    },
    cellWidth: {
      type: Number,
      default: 80,
    },
    showUnassigned: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectionStart: null,
      selectionEnd: null,
    }
  },
  computed: {
    colCount() {
      return this.timeSlots.length + 1
    },
    timeSlots() {
      const slots = []
      for (let h = this.startHour; h < this.endHour; h += 1) {
        const displayH = h >= 24 ? h - 24 : h
        slots.push({
          label: `${String(displayH).padStart(2, '0')}:00`,
          value: `${String(displayH).padStart(2, '0')}:00`,
          hour: h,
        })
        slots.push({
          label: '',
          value: `${String(displayH).padStart(2, '0')}:30`,
          hour: h,
          half: true,
        })
      }

      return slots
    },
    gridStyle() {
      const cols = this.timeSlots.length

      return {
        display: 'grid',
        gridTemplateColumns: `120px repeat(${cols}, ${this.cellWidth}px)`,
      }
    },
    rows() {
      const rows = []
      let gridRow = 2

      this.halls.forEach(hall => {
        const hallId = parseInt(hall.id, 10)
        rows.push({
          type: 'hall', id: hallId, name: hall.attributes?.name || hall.name, gridRow,
        })
        gridRow += 1

        const tableCount = hall.attributes?.tableCount || 0
        const prefix = hall.attributes?.tablePrefix || ''
        const suffix = hall.attributes?.tableSuffix || ''
        for (let tn = 1; tn <= tableCount; tn += 1) {
          rows.push({
            type: 'table', hallId, tableNumber: tn, label: `${prefix}${tn}${suffix}`, gridRow,
          })
          gridRow += 1
        }
      })

      const unassigned = this.showUnassigned
        ? this.reservations.filter(r => !r.attributes?.tableNumber)
        : []
      if (unassigned.length > 0) {
        rows.push({ type: 'unassigned-header', gridRow })
        gridRow += 1
        unassigned.forEach((res, idx) => {
          rows.push({
            type: 'unassigned', index: idx, label: '-', reservations: [res], gridRow,
          })
          gridRow += 1
        })
      }

      return rows
    },
  },
  methods: {
    timeToSlotIndex(timeStr) {
      if (!timeStr) return 0
      const match = timeStr.match(/(\d{2}):(\d{2})/)
      if (!match) return 0
      let h = parseInt(match[1], 10)
      const m = parseInt(match[2], 10)
      if (h < this.startHour && h < 6) h += 24

      return (h - this.startHour) * 2 + (m >= 30 ? 1 : 0)
    },
    timeToMinutes(timeStr) {
      if (!timeStr) return 0
      const match = timeStr.match(/(\d{2}):(\d{2})/)
      if (!match) return 0
      let h = parseInt(match[1], 10)
      const m = parseInt(match[2], 10)
      if (h < this.startHour && h < 6) h += 24

      return h * 60 + m
    },
    getBlockGridStyle(row, res) {
      const a = res.attributes || {}
      const startCol = this.timeToSlotIndex(a.startTime) + 2
      let endCol = startCol + 1
      if (a.endTime) {
        endCol = this.timeToSlotIndex(a.endTime) + 2
      }
      if (endCol <= startCol) endCol = startCol + 1

      return {
        gridRow: row.gridRow,
        gridColumn: `${startCol} / ${endCol}`,
      }
    },
    getReservationsForRow(row) {
      if (row.type === 'unassigned') return row.reservations || []
      if (row.type !== 'table') return []

      return this.reservations.filter(r => {
        const a = r.attributes || {}
        const rHallId = a.hall?.id ? parseInt(a.hall.id, 10) : null

        return rHallId === row.hallId && a.tableNumber === row.tableNumber
      })
    },
    clearSelection() {
      this.selectionStart = null
      this.selectionEnd = null
    },
    onCellClick(row, time) {
      if (!this.selectionStart || this.selectionEnd) {
        this.selectionEnd = null
        this.selectionStart = {
          hallId: row.hallId || null,
          tableNumber: row.tableNumber || null,
          time: time.value,
        }
      } else if (this.selectionStart.hallId === (row.hallId || null)
        && this.selectionStart.tableNumber === (row.tableNumber || null)) {
        const startTime = this.selectionStart.time
        const clickedTime = time.value
        const startMin = this.timeToMinutes(startTime)
        const clickedMin = this.timeToMinutes(clickedTime)
        const orderedStart = startMin <= clickedMin ? startTime : clickedTime
        const orderedEnd = startMin <= clickedMin ? clickedTime : startTime
        this.selectionEnd = orderedEnd
        this.$emit('cell-click', {
          hallId: row.hallId || null,
          tableNumber: row.tableNumber || null,
          startTime: orderedStart,
          endTime: this.nextSlotTime(orderedEnd),
        })
      } else {
        this.selectionEnd = null
        this.selectionStart = {
          hallId: row.hallId || null,
          tableNumber: row.tableNumber || null,
          time: time.value,
        }
      }
    },
    nextSlotTime(timeStr) {
      const match = timeStr.match(/(\d{2}):(\d{2})/)
      if (!match) return timeStr
      let h = parseInt(match[1], 10)
      let m = parseInt(match[2], 10)
      m += 30
      if (m >= 60) {
        m = 0
        h += 1
      }
      if (h >= 24) h -= 24

      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },
    isCellSelected(row, time) {
      if (!this.selectionStart) return false
      if (this.selectionStart.hallId !== (row.hallId || null)) return false
      if (this.selectionStart.tableNumber !== (row.tableNumber || null)) return false

      const startMin = this.timeToMinutes(this.selectionStart.time)
      const cellMin = this.timeToMinutes(time.value)

      if (this.selectionEnd) {
        const endMin = this.timeToMinutes(this.selectionEnd)

        return cellMin >= Math.min(startMin, endMin) && cellMin <= Math.max(startMin, endMin)
      }

      return cellMin === startMin
    },
  },
}
</script>

<style scoped>
.timeline-grid-wrapper {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.timeline-grid__corner {
  background: #f5f5f5;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  left: 0;
  z-index: 3;
}

.timeline-grid__header {
  text-align: center;
  font-size: 11px;
  color: #666;
  padding: 4px 0;
  border-bottom: 1px solid #e0e0e0;
  border-right: 1px solid #f0f0f0;
  background: #f5f5f5;
}

.timeline-grid__hall-label {
  background: #eeeeee;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  left: 0;
  z-index: 2;
}

.timeline-grid__row-label {
  font-size: 12px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #f0f0f0;
  background: white;
  min-height: 36px;
  position: sticky;
  left: 0;
  z-index: 2;
}

.timeline-grid__cell {
  border-right: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  min-height: 36px;
  cursor: pointer;
}

.timeline-grid__cell:hover {
  background: rgba(25, 118, 210, 0.04);
}

.timeline-grid__cell--selected {
  background: rgba(25, 118, 210, 0.15);
}

/* ダークテーマ対応（既定はライト向けの固定色のため上書きする） */
.theme--dark .timeline-grid-wrapper {
  border-color: rgba(231, 227, 252, 0.14);
}

.theme--dark .timeline-grid__corner,
.theme--dark .timeline-grid__header {
  background: #3b3559;
  border-color: rgba(231, 227, 252, 0.14);
  color: rgba(231, 227, 252, 0.68);
}

.theme--dark .timeline-grid__hall-label {
  background: #3b3559;
  border-color: rgba(231, 227, 252, 0.14);
  color: rgba(231, 227, 252, 0.87);
}

.theme--dark .timeline-grid__row-label {
  background: #312d4b;
  border-right-color: rgba(231, 227, 252, 0.14);
  border-bottom-color: rgba(231, 227, 252, 0.08);
  color: rgba(231, 227, 252, 0.87);
}

.theme--dark .timeline-grid__cell {
  border-right-color: rgba(231, 227, 252, 0.08);
  border-bottom-color: rgba(231, 227, 252, 0.08);
}

.theme--dark .timeline-grid__cell:hover {
  background: rgba(145, 85, 253, 0.1);
}

.theme--dark .timeline-grid__cell--selected {
  background: rgba(145, 85, 253, 0.24);
}
</style>
